import styled from '@emotion/styled'

import { useLoadUrl } from '@cash-web/shared-data-access-load-url'
import {
  boldTextTokenizer,
  emailTextTokenizer,
  headerTokenizer,
  preformattedPhoneNumberTokenizer,
  spacerTokenizer,
  TokenizersRegex,
} from '@cash-web/text-tokenizer'
import { createTokenizer, Tokenizer } from '@cash-web/text-tokenizer'

interface TokenizerOptions {
  textLink?: {
    target?: string
    useFlowLoadUrl?: boolean
  }
}

const TextLink = styled.a`
  color: inherit;
  text-decoration: underline;
  cursor: pointer;
`

/**
 * A React hook that returns a text tokenizer function to parse and format strings. Links are handled
 * via the shared `loadUrl` hook if `useFlowLoadUrl` is true.
 *
 * @returns a text tokenizer function that is compatible with journeys.
 */
export const useTextTokenizer = (options?: TokenizerOptions) => {
  const loadUrl = useLoadUrl()

  const { textLink } = options ?? {}
  const { target, useFlowLoadUrl = true } = textLink ?? {}

  const textLinkTokenizer: Tokenizer = {
    regex: TokenizersRegex.link,
    toToken: ([, text, href]: string[]) => (
      <TextLink
        href={href}
        key={text}
        target={textLink?.target}
        onClick={e => {
          if (useFlowLoadUrl) {
            e.preventDefault()
            loadUrl({ url: href, trackingName: text, target })
          }
        }}
      >
        {text}
      </TextLink>
    ),
  }

  return (text?: string) => {
    if (text == null) {
      return text
    }

    return createTokenizer([
      textLinkTokenizer,
      boldTextTokenizer,
      preformattedPhoneNumberTokenizer,
      emailTextTokenizer,
      headerTokenizer,
      spacerTokenizer,
    ])([text])
  }
}
